import { useState, useEffect } from "react";
import { FaCheck, FaEdit, FaTimes } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import ExportOptions from "../../ExportOptions";
import TableSkeleton from "../../TableSkeleton";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import Pagination from "../../Shared/Pagination";

const API_BASE = "https://api.muktihospital.com/api/medicine-group";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const AddMedicineGroupModal = ({ isOpen, onClose, onSave }) => {
  const [medicineGroupData, setMedicineGroupData] = useState({
    groupNames: [""],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setMedicineGroupData({ groupNames: [""] });
      setErrors({});
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const checkDuplicateName = async (groupName) => {
    try {
      const response = await axios.get(`${API_BASE}`, {
        headers: { "x-api-key": API_KEY },
      });
      const existingGroups = response.data.medicineGroups || response.data.doses || response.data || [];
      return existingGroups.some(
        (group) => group.groupName.toLowerCase() === groupName.toLowerCase()
      );
    } catch (error) {
      console.error("Error checking duplicate name:", error);
      return false; // Assume no duplicate in case of error to avoid blocking save
    }
  };

  const validateForm = async () => {
    const newErrors = {};
    let hasError = false;

    for (let index = 0; index < medicineGroupData.groupNames.length; index++) {
      const groupName = medicineGroupData.groupNames[index];
      if (!groupName.trim()) {
        newErrors[`groupName${index}`] = `Medicine Group Name ${index + 1} is required`;
        hasError = true;
      } else {
        const isDuplicate = await checkDuplicateName(groupName.trim());
        if (isDuplicate) {
          newErrors[`groupName${index}`] = `Medicine Group Name "${groupName.trim()}" already exists`;
          hasError = true;
        }
      }
    }

    setErrors(newErrors);
    if (hasError) {
      toast.error("Please fix the errors in the form", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    return !hasError;
  };

  const handleGroupNameChange = (index, value) => {
    const updatedGroupNames = [...medicineGroupData.groupNames];
    updatedGroupNames[index] = value;
    setMedicineGroupData((prevData) => ({
      ...prevData,
      groupNames: updatedGroupNames,
    }));
    if (value.trim()) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`groupName${index}`];
        return newErrors;
      });
    }
  };

  const addGroupNameField = () => {
    setMedicineGroupData((prevData) => ({
      ...prevData,
      groupNames: [...prevData.groupNames, ""],
    }));
  };

  const removeGroupNameField = (index) => {
    setMedicineGroupData((prevData) => ({
      ...prevData,
      groupNames: prevData.groupNames.filter((_, i) => i !== index),
    }));
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`groupName${index}`];
      return newErrors;
    });
  };

  const handleSave = async () => {
    if (!(await validateForm())) return;

    setIsLoading(true);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(
        () => source.cancel("Request timed out"),
        10000
      );
      const createdMedicineGroups = [];
      for (const groupName of medicineGroupData.groupNames) {
        // console.log(`Sending POST request to ${API_BASE}/add with payload:`, {
        //   groupName: groupName.trim(),
        // });
        const response = await axios.post(
          `${API_BASE}/add`,
          { groupName: groupName.trim() },
          {
            headers: {
              "x-api-key": API_KEY,
              "Content-Type": "application/json",
            },
            cancelToken: source.token,
          }
        );
        // console.log("Add medicine group response:", response.data);
        if (response.status === 201)
          createdMedicineGroups.push(
            response.data.medicineGroup || response.data.dose || response.data
          );
      }
      clearTimeout(timeout);
      toast.success(
        `${createdMedicineGroups.length} medicine group(s) added successfully!`,
        { position: "top-right", autoClose: 3000 }
      );
      // console.log("Created medicine groups:", createdMedicineGroups);
      onSave(createdMedicineGroups);
      setMedicineGroupData({ groupNames: [""] });
      setErrors({});
      onClose();
    } catch (error) {
      if (axios.isCancel(error)) {
        toast.error("Medicine group add request timed out", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        console.error(
          "Add Medicine Group Error:",
          error.response?.data || error.message
        );
        toast.error(
          error.response?.data?.error || "Failed to add medicine group.",
          { position: "top-right", autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-96">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Add Medicine Group</h2>
        </div>
        <div className="p-4">
          <label className="label">
            Medicine Group Name <span className="text-red-500">*</span>
          </label>
          {medicineGroupData.groupNames.map((groupName, index) => (
            <div key={index} className="grid grid-cols-5 gap-2 mb-4">
              <div className="col-span-4">
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => handleGroupNameChange(index, e.target.value)}
                  className={`input-field ${
                    errors[`groupName${index}`] ? "border-red-500" : ""
                  }`}
                  placeholder="Medicine Group Name"
                  disabled={isLoading}
                />
                {errors[`groupName${index}`] && (
                  <span className="text-red-500 text-xs mt-1">
                    {errors[`groupName${index}`]}
                  </span>
                )}
              </div>
              {medicineGroupData.groupNames.length > 1 && (
                <div className="col-span-1 flex items-end">
                  <button
                    onClick={() => removeGroupNameField(index)}
                    className="p-2 text-red-500 hover:text-red-700"
                    disabled={isLoading}
                  >
                    <FaTimes />
                  </button>
                </div>
              )}
            </div>
          ))}
          <button
            onClick={addGroupNameField}
            className="flex items-center mt-3 gap-1 bg-M-primary-color/90 text-sm text-white py-1 px-3 rounded-sm hover:bg-M-primary-color"
            disabled={isLoading}
          >
            Add
          </button>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D]"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
const EditMedicineGroupModal = ({ isOpen, onClose, onSave, medicineGroup }) => {
  const [groupName, setGroupName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (medicineGroup) setGroupName(medicineGroup.groupName || "");
  }, [medicineGroup]);

  if (!isOpen) return null;

  const checkDuplicateName = async (groupName, currentId) => {
    try {
      const response = await axios.get(`${API_BASE}`, {
        headers: { "x-api-key": API_KEY },
      });
      const existingGroups = response.data.medicineGroups || response.data.doses || response.data || [];
      return existingGroups.some(
        (group) =>
          group.groupName.toLowerCase() === groupName.toLowerCase() &&
          group.id !== currentId
      );
    } catch (error) {
      console.error("Error checking duplicate name:", error);
      return false; // Assume no duplicate in case of error to avoid blocking save
    }
  };

  const handleSave = async () => {
    if (!groupName.trim()) {
      setError("Medicine Group Name is required");
      toast.error("Medicine Group name is required", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    const isDuplicate = await checkDuplicateName(groupName.trim(), medicineGroup.id);
    if (isDuplicate) {
      setError(`Medicine Group Name "${groupName.trim()}" already exists`);
      toast.error(`Medicine Group Name "${groupName.trim()}" already exists`, {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsLoading(true);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(
        () => source.cancel("Request timed out"),
        10000
      );
      // console.log(
      //   `Sending PUT request to ${API_BASE}/edit/${medicineGroup.id} with payload:`,
      //   { groupName }
      // );
      const response = await axios.put(
        `${API_BASE}/edit/${medicineGroup.id}`,
        { groupName },
        {
          headers: { "x-api-key": API_KEY, "Content-Type": "application/json" },
          cancelToken: source.token,
        }
      );
      clearTimeout(timeout);
      // console.log("Update medicine group response:", response.data);
      if (response.status === 200) {
        toast.success("Medicine Group updated successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        onSave(
          medicineGroup.id,
          response.data.medicineGroup || response.data.dose || response.data
        );
        onClose();
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        toast.error("Medicine group update request timed out", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        console.error(
          "Edit Medicine Group Error:",
          error.response?.data || error.message
        );
        toast.error(
          error.response?.data?.error || "Failed to update medicine group.",
          { position: "top-right", autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <div className="bg-white rounded-lg shadow-lg w-96">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">Edit Medicine Group</h2>
        </div>
        <div className="p-4">
          <div className="mb-4">
            <label className="label">
              Medicine Group Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={groupName}
              onChange={(e) => {
                setGroupName(e.target.value);
                setError("");
              }}
              className={`input-field ${error ? "border-red-500" : ""}`}
              placeholder="Medicine Group Name"
              disabled={isLoading}
            />
            {error && (
              <span className="text-red-500 text-xs mt-1">{error}</span>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D]"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
const MedicineGroup = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [medicineGroupToEdit, setMedicineGroupToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [medicineGroups, setMedicineGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const itemsPerPage = 10;
  const filteredMedicineGroups = medicineGroups.filter(
    (group) =>
      group &&
      typeof group.groupName === "string" &&
      group.groupName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredMedicineGroups.length / itemsPerPage);
  const currentData = filteredMedicineGroups
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((group, index) => ({
      ...group,
      serialNumber: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  useEffect(() => {
    const fetchMedicineGroups = async () => {
    setIsLoading(true);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(
        () => source.cancel("Request timed out"),
        10000
      );
      // console.log(`Fetching Medicine Groups from ${API_BASE}`);
      const response = await axios.get(API_BASE, {
        headers: { "x-api-key": API_KEY },
        cancelToken: source.token,
      });
      clearTimeout(timeout);
      // console.log("Fetch medicine groups response:", response.data);
      const data =
        response.data.medicineGroups ||
        response.data.doses ||
        response.data ||
        [];
      // console.log("Parsed medicine groups:", data);
      const sortedData = Array.isArray(data) ? data.sort((a, b) => b.id - a.id) : [];
      setMedicineGroups(sortedData);
      setError(null);
    } catch (error) {
      if (axios.isCancel(error)) {
        setError("Medicine group fetch request timed out");
        toast.error("Medicine group fetch request timed out", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        const errorMessage =
          error.response?.data?.error ||
          error.message ||
          "Failed to fetch medicine groups.";
        console.error("Fetch Medicine Groups Error:", error);
        // console.log("Error details:", {
        //   status: error.response?.status,
        //   data: error.response?.data,
        //   message: error.message,
        // });
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } finally {
      setIsLoading(false);
      // console.log("Current medicineGroups state:", medicineGroups);
    }
  };
    fetchMedicineGroups();
  }, []);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      // console.log(`Adjusting page from ${currentPage} to ${totalPages}`);
      setCurrentPage(totalPages);
    } else if (totalPages === 0) {
      // console.log("No data, resetting to page 1");
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const handleSelectAll = (e) => {
    setSelectedRows(
      e.target.checked ? currentData.map((group) => group.id) : []
    );
  };

  const handleRowSelect = (groupId) => {
    setSelectedRows((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleDelete = async (groupIds) => {
    setIsLoading(true);
    try {
      const source = axios.CancelToken.source();
      const timeout = setTimeout(
        () => source.cancel("Request timed out"),
        10000
      );
      const requests = Array.isArray(groupIds)
        ? groupIds.map((id) =>
            axios.delete(`${API_BASE}/delete/${id}`, {
              headers: { "x-api-key": API_KEY },
              cancelToken: source.token,
            })
          )
        : [
            axios.delete(`${API_BASE}/delete/${groupIds}`, {
              headers: { "x-api-key": API_KEY },
              cancelToken: source.token,
            }),
          ];
      const responses = await Promise.all(requests);
      clearTimeout(timeout);
      // console.log(
      //   "Delete medicine groups response:",
      //   responses.map((res) => res.data)
      // );
      setMedicineGroups((prev) =>
        prev.filter((group) => !groupIds.includes(group.id))
      );
      setSelectedRows([]);
      toast.success(
        `${
          Array.isArray(groupIds) ? groupIds.length : 1
        } medicine group(s) deleted successfully!`,
        { position: "top-right", autoClose: 3000 }
      );
    } catch (error) {
      if (axios.isCancel(error)) {
        toast.error("Medicine group delete request timed out", {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        console.error(
          "Delete Medicine Groups Error:",
          error.response?.data || error.message
        );
        toast.error(
          error.response?.data?.error || "Failed to delete medicine group(s).",
          { position: "top-right", autoClose: 3000 }
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (group) => {
    setMedicineGroupToEdit(group);
    setEditModalOpen(true);
  };

  const handleUpdateMedicineGroup = (id, updatedGroup) => {
    setMedicineGroups((prev) =>
      prev.map((group) =>
        group.id === id
          ? { ...group, groupName: updatedGroup.groupName }
          : group
      )
    );
  };

  const handleAddMedicineGroup = (newGroups) => {
    setMedicineGroups((prev) => [...newGroups, ...prev]);
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = ["Serial Number", "Medicine Group Name"];
  const dataMapper = (item) =>
    [item.serialNumber, item.groupName || "-"].join("\t");

  const skeletonColumns = [
    {
      width: "40px",
      height: "16px",
      className: "text-left print-hidden-in-print",
      skeletonClassName: "my-5 mx-3",
    },
    {
      width: "80px",
      height: "16px",
      className:
        "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "200px",
      height: "16px",
      className:
        "text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3",
      skeletonClassName: "",
    },
    {
      width: "100px",
      height: "16px",
      className: "text-right p-3 print-hidden-in-print",
      skeletonClassName: "ml-auto",
    },
  ];

  if (error) {
    return (
      <div className="text-center p-4 text-red-600 print-hidden">
        {error}
        <button
          onClick={() => window.location.reload()}
          className="ml-4 bg-M-primary-color/90 text-sm text-white py-1 px-3 rounded-md hover:bg-M-primary-color"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <ToastContainer />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 flex-1">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Medicine Group List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                disabled={isLoading}
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-full sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by Medicine Group name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              disabled={isLoading}
            />
            <div className="inline-flex items-center justify-between w-full gap-3">
              <button
                onClick={() => setAddModalOpen(true)}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
                disabled={isLoading}
              >
                + Add Medicine Group
              </button>
              <div className="relative" style={{ overflow: "visible" }}>
                <ExportOptions
                  data={currentData}
                  dropdownOpen={dropdownOpen}
                  toggleDropdown={toggleDropdown}
                  title="Medicine Group List"
                  headers={headers}
                  dataMapper={dataMapper}
                />
              </div>
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            {isLoading ? (
              <div className="print-hidden">
                <table className="min-w-[781px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial Number
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Medicine Group Name
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : currentData.length === 0 ? (
              <div className="print-hidden">
                <table className="min-w-[781px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]"></div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial Number
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Medicine Group Name
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td
                        colSpan="4"
                        className="text-center py-5 text-gray-600 print-hidden"
                      >
                        No medicine groups found
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[781px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            id="agreement"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                              selectedRows.length === currentData.length &&
                              currentData.length > 0
                            }
                            disabled={isLoading}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="agreement"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Serial Number
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        Medicine Group Name
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((group, index) => (
                      <tr key={group.id} className="border-b hover:bg-gray-50">
                        <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                          <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={"medicineGroup" + index}
                              className="hidden peer print-hidden"
                              checked={selectedRows.includes(group.id)}
                              onChange={() => handleRowSelect(group.id)}
                              disabled={isLoading}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                            <label
                              htmlFor={"medicineGroup" + index}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                            ></label>
                          </div>
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {group.serialNumber}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          <p className="line-clamp-2">
                            {group.groupName || "-"}
                          </p>
                        </td>
                        <td className="p-3 flex justify-end shrink-0 space-x-2 print-hidden-in-print">
                          <button
                            onClick={() => handleEdit(group)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            disabled={isLoading}
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRows([group.id]);
                              setDeleteModalOpen(true);
                            }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            disabled={isLoading}
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="print-hidden">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={filteredMedicineGroups.length}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          </div>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="medicine group"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
        message={`Do you really want to delete ${selectedRows.length} medicine group?`}
      />
      <AddMedicineGroupModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddMedicineGroup}
      />
      <EditMedicineGroupModal
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleUpdateMedicineGroup}
        medicineGroup={medicineGroupToEdit}
      />
    </div>
  );
};

export default MedicineGroup;
